import {
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CopyCoordinates } from '@epicuro-next/features/advanced-mdo-grid';
import { plural } from '@epicuro-next/platform/i18n';
import { CellInventory } from '@epicuro-next/state/config-strip-state';
import { BasketAccountAddress, Prepack } from 'graphql-api/gql-api-interfaces';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

export interface IKeyValueOfType<T> {
  [key: string]: T;
}

@Directive()
export abstract class BaseAdvancedGridComponent implements OnInit, OnDestroy {
  protected selectedVariant$ = new Subject<string>();

  protected destroy$ = new Subject<void>();

  @Input() public shipTos: BasketAccountAddress[];
  @Input() public selectedScale: string;
  @Input() public scaleSets: string[];
  @Input() public availableInventories: IKeyValueOfType<CellInventory>;
  @Input() public deliveryItems: IKeyValueOfType<
    IKeyValueOfType<{ quantity: number; note: string }>
  >;
  @Input() public titleAttributes: string[];
  // indicates if grid is in compact mode
  @Input() public portable: boolean;
  @Input() public shopTotals: { [key: string]: number };
  @Input() public differentMaterialDimensions = false;
  @Input() public skipInventoryCheck = false;
  @Output() public quantityChanged = new EventEmitter();
  @Output() public copyQuantities = new EventEmitter();
  @Output() public clearShop = new EventEmitter();
  @Output() public variantSelected = new EventEmitter();

  public ngOnInit() {
    this.selectedVariant$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((variant) => {
        this.variantSelected.emit(variant);
      });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public abstract onCopy({ from, to }: CopyCoordinates): void;

  public getQuantityLeft(prepack: Prepack) {
    return this.getMaxAvailableQuantity(prepack);
  }

  public getMaxAvailableQuantity(prepack: Prepack) {
    return this.availableInventories[prepack.productId]?.totals || 0;
  }

  public lowInvetoryWarningText(prepack: Prepack) {
    const quantityLeft = this.getQuantityLeft(prepack);
    return plural(
      quantityLeft,
      {
        0: $localize`:ConfigStrip|inbound quantity warning@@inboundQuantityWarningZero:No more units available`,
        1: $localize`:ConfigStrip|inbound quantity warning@@inboundQuantityWarningOne:${quantityLeft} unit left`,
      },
      $localize`:ConfigStrip|inbound quantity warning@@inboundQuantityWarningMore:${quantityLeft} units left`,
    );
  }

  public getDiscount(prepack: Prepack) {
    return prepack.priceRanges?.[0]?.discount;
  }

  public selectVariant({ materialId }: { materialId: string }) {
    this.selectedVariant$.next(materialId);
  }
}
